import React from 'react';
import NavigationHeader from '../components/NavigationHeader';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Shield, Clock, Award, Globe, Trophy, CheckCircle2, 
  Users, Activity, GraduationCap, MapPin, ArrowRight, Star 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { astonVillaData } from '../siteData';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const iconMap = {
  Award: Award,
  Clock: Clock,
  Globe: Globe,
  Trophy: Trophy,
  Shield: Shield
};

const AstonVillaPage = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);
  
  // Get translated arrays/objects
  const features = t('astonVilla.features') || astonVillaData.features;
  const schedule = t('astonVilla.schedule') || astonVillaData.schedule;
  const included = t('astonVilla.included') || astonVillaData.included;
  const workshopTopics = t('astonVilla.workshopTopics') || astonVillaData.training?.workshops?.topics || [];
  const description = t('astonVilla.description') || astonVillaData.description;
  const englishMethodology = t('astonVilla.englishMethodology') || astonVillaData.englishProgram?.methodology;
  const englishObjective = t('astonVilla.englishObjective') || astonVillaData.englishProgram?.objective;
  const workshopLedBy = t('astonVilla.workshopLedBy') || astonVillaData.training?.workshops?.ledBy;

  const langPrefix = language === 'en' ? '' : `/${language}`;

  return (
    <div className="min-h-screen">
      <NavigationHeader />
      
      {/* Hero Section */}
      <section className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${astonVillaData.images[0]})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-purple-900/90 via-purple-800/80 to-red-900/70"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <div className="max-w-4xl">
            <div className="mb-8">
              <img 
                src={astonVillaData.logo} 
                alt="Aston Villa Foundation" 
                className="h-32 w-auto"
              />
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
              {t('astonVilla.heroTitle')}
            </h1>
            
            <p className="text-xl md:text-2xl mb-8 text-gray-200 font-medium">
              {t('astonVilla.heroTagline')}
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3">
                <span className="font-bold">{t('astonVilla.ages')} {astonVillaData.ageRange}</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3">
                <span className="font-bold">{t('astonVilla.hoursTrainingWeek')}</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3">
                <span className="font-bold">{t('astonVilla.englishClassesIncluded')}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to={`${langPrefix}/prices`}>
                <Button 
                  size="lg" 
                  className="bg-white text-purple-900 hover:bg-gray-100 font-bold text-lg px-10 py-7 rounded-xl transform hover:scale-105 transition-all duration-200 shadow-2xl"
                >
                  {t('astonVilla.viewPrices')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to={`${langPrefix}/signup`}>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-purple-900 font-bold text-lg px-10 py-7 rounded-xl transform hover:scale-105 transition-all duration-200"
                >
                  {t('astonVilla.signUpNow')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Program Overview with Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={astonVillaData.images[3]} 
                alt="Aston Villa Training"
                className="w-full h-full object-cover"
              />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">
                {t('astonVilla.programOverview')}
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                {description}
              </p>

              <div className="grid grid-cols-2 gap-4">
                <Card className="p-6 text-center bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200">
                  <Users className="h-10 w-10 mx-auto mb-3 text-purple-800" />
                  <p className="text-sm text-gray-600 mb-1">{t('astonVilla.ages')}</p>
                  <p className="text-2xl font-black text-gray-900">{astonVillaData.ageRange}</p>
                </Card>
                <Card className="p-6 text-center bg-gradient-to-br from-red-50 to-white border-2 border-red-200">
                  <Clock className="h-10 w-10 mx-auto mb-3 text-red-700" />
                  <p className="text-sm text-gray-600 mb-1">{t('astonVilla.duration')}</p>
                  <p className="text-2xl font-black text-gray-900">1-2 {t('astonVilla.weeks')}</p>
                </Card>
                <Card className="p-6 text-center bg-gradient-to-br from-green-50 to-white border-2 border-green-200">
                  <Activity className="h-10 w-10 mx-auto mb-3 text-green-700" />
                  <p className="text-sm text-gray-600 mb-1">{t('astonVilla.training')}</p>
                  <p className="text-2xl font-black text-gray-900">20 {t('astonVilla.hrsWeek')}</p>
                </Card>
                <Card className="p-6 text-center bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200">
                  <GraduationCap className="h-10 w-10 mx-auto mb-3 text-blue-700" />
                  <p className="text-sm text-gray-600 mb-1">{t('astonVilla.english')}</p>
                  <p className="text-2xl font-black text-gray-900">7.5 {t('astonVilla.hrsWeek')}</p>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              {t('astonVilla.programHighlights')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('astonVilla.programHighlightsDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const icons = [Award, Clock, Globe, Trophy];
              const Icon = icons[index] || Award;
              return (
                <Card 
                  key={index}
                  className="p-6 text-center hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-8 w-8 text-purple-800" />
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {feature.description}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Training Program */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Shield className="h-12 w-12 text-purple-400 mb-6" />
              <h2 className="text-4xl md:text-5xl font-black mb-6">
                {t('astonVilla.eliteFootballTraining')}
              </h2>
              <p className="text-xl text-gray-300 mb-8">
                {astonVillaData.training.hoursPerWeek} {t('astonVilla.eliteFootballTrainingDesc')}
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="text-3xl font-black text-purple-400">{astonVillaData.training.hoursPerWeek}</div>
                  <div className="text-xs text-gray-300 mt-1">{t('astonVilla.hoursTrainingWeekLabel')}</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div className="text-3xl font-black text-purple-400">{astonVillaData.training.workshops.hours}</div>
                  <div className="text-xs text-gray-300 mt-1">{t('astonVilla.hoursWorkshopsWeek')}</div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6">
                <h3 className="text-xl font-black mb-4">{t('astonVilla.professionalWorkshopsInclude')}</h3>
                <ul className="space-y-2">
                  {workshopTopics.map((topic, idx) => (
                    <li key={idx} className="flex items-center text-gray-200">
                      <CheckCircle2 className="h-5 w-5 text-purple-400 mr-3" />
                      {topic}
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-gray-400 mt-4">
                  {t('astonVilla.ledBy')} {workshopLedBy}
                </p>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={astonVillaData.images[1]} 
                alt="Aston Villa Training"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* English Program */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <Card className="bg-white/10 backdrop-blur-lg border-white/20 p-8">
                <GraduationCap className="h-12 w-12 mb-6" />
                <h3 className="text-3xl font-black mb-6">{t('astonVilla.dynamicEnglishClasses')}</h3>
                <div className="space-y-4 text-blue-100">
                  <p><strong className="text-white">{t('astonVilla.hoursPerWeek')}</strong> {astonVillaData.englishProgram.hoursPerWeek}</p>
                  <p><strong className="text-white">{t('astonVilla.groupSize')}</strong> {astonVillaData.englishProgram.groupSize}</p>
                  <p><strong className="text-white">{t('astonVilla.levels')}</strong> {astonVillaData.englishProgram.levels} {t('astonVilla.differentGroupsBasedOnTest')}</p>
                  <p><strong className="text-white">{t('astonVilla.certificate')}</strong> {t('astonVilla.diplomaUponCompletion')}</p>
                </div>
                <div className="mt-6 bg-white/20 rounded-lg p-4">
                  <p className="text-sm font-semibold">
                    {englishObjective}
                  </p>
                </div>
              </Card>
            </div>

            <div className="order-1 lg:order-2">
              <h2 className="text-4xl md:text-5xl font-black mb-6">
                {t('astonVilla.englishLanguageImmersion')}
              </h2>
              <p className="text-xl text-blue-100 mb-8">
                {englishMethodology}
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-blue-300 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-blue-100">{t('astonVilla.grammarConversation')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-blue-300 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-blue-100">{t('astonVilla.smallGroupsTailored')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-blue-300 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-blue-100">{t('astonVilla.initialLevelTest')}</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-blue-300 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-blue-100">{t('astonVilla.focusOnOvercomingFears')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Daily Schedule */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              {t('astonVilla.dailySchedule')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('astonVilla.dailyScheduleDesc')}
            </p>
          </div>

          <Card className="p-8 bg-gradient-to-br from-gray-50 to-white max-w-4xl mx-auto">
            <div className="space-y-4">
              {schedule.map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-start pb-4 border-b border-gray-200 last:border-0"
                >
                  <div className="flex-shrink-0 w-32">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 text-purple-600 mr-2" />
                      <span className="text-sm font-bold text-gray-900">{item.time}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-base font-black text-gray-900">{item.activity}</p>
                    {item.detail && (
                      <p className="text-sm text-gray-600">{item.detail}</p>
                    )}
                    {item.location && (
                      <p className="text-xs text-gray-500 mt-1">
                        <MapPin className="h-3 w-3 inline mr-1" />
                        {item.location}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-gradient-to-br from-purple-900 to-red-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black mb-4">
              {t('astonVilla.whatsIncluded')}
            </h2>
            <p className="text-xl text-purple-200">
              {t('astonVilla.whatsIncludedDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl mx-auto mb-16">
            {included.map((item, index) => (
              <div 
                key={index}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-4 flex items-start border border-white/20"
              >
                <CheckCircle2 className="h-5 w-5 text-yellow-400 mr-3 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing & Dates Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              {t('astonVilla.pricingDates')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('astonVilla.pricingDatesDesc')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
            <Card className="p-8 hover:shadow-2xl transition-all duration-300 border-2 border-purple-200">
              <div className="text-center mb-6">
                <h3 className="text-3xl font-black text-gray-900 mb-2">1 {t('astonVilla.week')}</h3>
                <div className="text-5xl font-black text-purple-800 mb-2">£1,895</div>
                <p className="text-sm text-gray-600">{t('astonVilla.fullBoardTrainingKit')}</p>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start text-sm">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('astonVilla.upTo20HoursTraining')}</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('astonVilla.englishClasses')}</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('astonVilla.officialTrainingKit')}</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('astonVilla.stadiumExcursion')}</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('astonVilla.completeEvaluationReport')}</span>
                </li>
              </ul>
              <Link to={`${langPrefix}/signup`}>
                <Button className="w-full bg-gradient-to-r from-purple-800 to-red-700 hover:from-purple-900 hover:to-red-800 text-white font-bold py-6">
                  {t('astonVilla.signUpNow')}
                </Button>
              </Link>
            </Card>

            <Card className="p-8 border-4 border-purple-600 shadow-2xl transform scale-105 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="bg-purple-600 text-white px-6 py-2 rounded-full text-xs font-black flex items-center shadow-lg">
                  <Star className="h-4 w-4 mr-1 fill-current" />
                  {t('astonVilla.mostPopular')}
                </div>
              </div>
              <div className="text-center mb-6">
                <h3 className="text-3xl font-black text-gray-900 mb-2">2 {t('astonVilla.weeks')}</h3>
                <div className="text-5xl font-black text-purple-800 mb-2">£2,895</div>
                <p className="text-sm text-gray-600">{t('astonVilla.fullBoardTrainingKit')}</p>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start text-sm">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('astonVilla.upTo20HoursTraining')}</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('astonVilla.englishClasses')}</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('astonVilla.officialTrainingKit')}</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('astonVilla.stadiumExcursion')}</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('astonVilla.sportsWorkshops')}</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('astonVilla.competitiveMatches')}</span>
                </li>
                <li className="flex items-start text-sm">
                  <CheckCircle2 className="h-5 w-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>{t('astonVilla.completeEvaluationReport')}</span>
                </li>
              </ul>
              <Link to={`${langPrefix}/signup`}>
                <Button className="w-full bg-gradient-to-r from-purple-800 to-red-700 hover:from-purple-900 hover:to-red-800 text-white font-bold py-6">
                  {t('astonVilla.signUpNow')}
                </Button>
              </Link>
            </Card>
          </div>

          {/* Start Dates */}
          <Card className="p-8 bg-gradient-to-br from-purple-50 to-red-50 border-2 border-purple-200 max-w-3xl mx-auto">
            <h3 className="text-2xl font-black text-gray-900 mb-2 text-center">{t('astonVilla.startDates2026')}</h3>
            <p className="text-sm text-center text-orange-600 font-semibold mb-6">{t('prices.limitedAvailability')}</p>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 text-center border-2 border-orange-200 hover:border-orange-400 transition-all shadow-lg">
                <div className="text-2xl font-black text-purple-800 mb-2">19 July</div>
                <div className="inline-flex items-center gap-1 bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold px-2.5 py-1 rounded-full">
                  {t('prices.onlyXSpotsLeft').replace('{x}', '8')}
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 text-center border-2 border-orange-200 hover:border-orange-400 transition-all shadow-lg">
                <div className="text-2xl font-black text-purple-800 mb-2">26 July</div>
                <div className="inline-flex items-center gap-1 bg-orange-50 border border-orange-200 text-orange-700 text-xs font-bold px-2.5 py-1 rounded-full">
                  {t('prices.onlyXSpotsLeft').replace('{x}', '8')}
                </div>
              </div>
            </div>
            <p className="text-center text-sm text-gray-600 mt-6">
              {t('astonVilla.registrationFee')} £100 | {t('astonVilla.initialDeposit')} 50% | {t('astonVilla.freeCancellationUntil')}
            </p>
          </Card>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              {t('astonVilla.experienceProgram')}
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              {t('astonVilla.experienceProgramDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {astonVillaData.images.slice(1).map((image, index) => (
              <div 
                key={index}
                className="relative overflow-hidden rounded-xl group cursor-pointer h-80"
              >
                <img 
                  src={image} 
                  alt={`Aston Villa Training ${index + 1}`}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AstonVillaPage;
